import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus } from 'lucide-react';
import { SectionLabel, SectionHeading } from './ui/UI';
import { BlurText } from './animations/BlurText';
import { Spotlight } from './animations/Spotlight';
const faqs = [{
  q: 'What is Periscan?',
  a: 'Periscan is a self-service Automated Security Validation platform. It helps teams validate exposure, controls, attack paths, AI applications, and remediation outcomes.'
}, {
  q: 'How does Periscan work?',
  a: 'Periscan connects to cloud, identity, SaaS, code, AI apps, security tools, ticketing systems, and optional internal runners. It builds an evidence model, runs validation missions, tests exposure and controls, recommends fixes, re-tests remediation, and generates proof.'
}, {
  q: 'Is Periscan a vulnerability scanner?',
  a: 'No. Periscan may use vulnerability data, but the product is not a scanner dashboard. Periscan validates which exposures matter and whether the risk is fixed.'
}, {
  q: 'Is Periscan BAS?',
  a: 'Partly. Periscan includes BAS-lite control validation, but it also validates exposure, attack paths, AI apps, remediation, and evidence.'
}, {
  q: 'Is Periscan AEV?',
  a: 'Yes. Periscan is built for adversarial exposure validation: proving whether attack scenarios, exposures, and control gaps are real in your environment.'
}, {
  q: 'Where does AI fit?',
  a: 'AI helps Periscan understand context, choose what to validate, interpret results, recommend fixes, and prepare evidence. It is not a magic button. The validation still has to prove itself.'
}, {
  q: 'What are Periscan Operators?',
  a: 'Periscan Operators are AI-assisted validation workflows for red-team testing, blue-team validation, exposure analysis, remediation, evidence, and AI app security. They operate inside customer-approved scope with policy controls and evidence requirements.'
}, {
  q: 'What is a Validation Snapshot?',
  a: 'A focused first report. It shows top validated exposures, attack paths, control observations, remediation steps, and verification guidance.'
}, {
  q: 'How does Periscan connect to my environment?',
  a: 'Start with API connectors and external validation. Add an internal runner when you want deeper inside-out validation. No inbound firewall rules are required for the runner.'
}, {
  q: 'Is it safe?',
  a: 'Periscan is built around verified scope, customer authorization, policy controls, rate limits, audit logs, no destructive actions, and no real data exfiltration. Self-service does not mean reckless.'
}, {
  q: 'How is pricing structured?',
  a: 'Periscan will use SaaS-style pricing based on what you validate. Validated assets. Identities. Control sources. AI apps. Internal runners. Evidence workflows. MSSP client tenants. Final pricing will be shaped during early access.'
}, {
  q: 'When is Periscan available?',
  a: 'Periscan is preparing for early access in 2026. Request access if you want to help shape the product before general availability.'
}];
export function FAQ() {
  const [open, setOpen] = useState<number | null>(0);
  return <section className="relative w-full py-32 border-t border-white/5" data-id="element-387">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-388">
        <SectionLabel number="23" label="FAQ" className="mb-8" data-id="element-389" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-390">
          <div className="lg:col-span-4" data-id="element-391">
            <motion.h2 initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.6
          }} className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-[-0.02em] leading-[1.1] text-white" data-id="element-392">
              Reasonable questions.
              <br data-id="element-393" />
              <span className="text-white/40" data-id="element-394">Direct answers.</span>
            </motion.h2>
            <p className="mt-6 text-base text-white/60 leading-relaxed" data-id="element-395">
              No marketing word salad. Just plain answers about validation, AI
              operators, pricing, and access.
            </p>
          </div>

          <div className="lg:col-span-8" data-id="element-396">
            <div className="border-t border-white/5" data-id="element-397">
              {faqs.map((faq, i) => {
              const isOpen = open === i;
              return <div key={i} className="border-b border-white/5" data-id="element-398">
                    <button onClick={() => setOpen(isOpen ? null : i)} aria-expanded={isOpen} className="w-full flex items-center justify-between gap-6 py-6 text-left group" data-id="element-399">
                      <span className="text-lg md:text-xl font-medium text-white pr-4 group-hover:text-[#1a8fd4] transition-colors" data-id="element-400">
                        {faq.q}
                      </span>
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full border flex items-center justify-center transition-all ${isOpen ? 'bg-[#1a8fd4] border-[#1a8fd4] rotate-45' : 'border-white/10 group-hover:border-white/30'}`} data-id="element-401">
                        <Plus className={`w-4 h-4 transition-colors ${isOpen ? 'text-white' : 'text-white/60'}`} data-id="element-402" />
                      </div>
                    </button>
                    <AnimatePresence data-id="element-403">
                      {isOpen && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3
                  }} className="overflow-hidden" data-id="element-404">
                          <p className="pb-6 pr-12 text-base text-white/60 leading-relaxed" data-id="element-405">
                            {faq.a}
                          </p>
                        </motion.div>}
                    </AnimatePresence>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}